import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Edit2, Trash2, Search, X, Loader2, RefreshCw,
  Eye, Copy, Check, Package, ShoppingCart, Hash,
  CheckCircle, XCircle, Grid, List, TrendingUp, Percent,
  ToggleLeft, ToggleRight, ChevronDown, ChevronUp,
} from 'lucide-react';
import { Input }    from '@/components/ui/input';
import { Label }    from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch }   from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem,
  SelectTrigger, SelectValue,
} from '@/components/ui/select';
import api       from '@/lib/api';
import { toast } from 'sonner';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const CATALOG_CATEGORIES = [
  { value: 'general',     label: 'General',       emoji: '📦', color: 'bg-gray-100 text-gray-700 border-gray-200' },
  { value: 'clothing',    label: 'Clothing',      emoji: '👕', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  { value: 'electronics', label: 'Electronics',   emoji: '📱', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'food',        label: 'Food & Drinks', emoji: '🍔', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  { value: 'beauty',      label: 'Beauty',        emoji: '💄', color: 'bg-pink-100 text-pink-700 border-pink-200' },
  { value: 'home',        label: 'Home & Living', emoji: '🏠', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { value: 'sports',      label: 'Sports',        emoji: '⚽', color: 'bg-green-100 text-green-700 border-green-200' },
  { value: 'books',       label: 'Books',         emoji: '📚', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  { value: 'toys',        label: 'Toys & Kids',   emoji: '🧸', color: 'bg-red-100 text-red-700 border-red-200' },
  { value: 'services',    label: 'Services',      emoji: '🛠️', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
];

const CAT_GRADIENTS = {
  clothing:    'from-purple-400 to-pink-400',
  electronics: 'from-blue-400 to-indigo-400',
  food:        'from-orange-400 to-red-400',
  beauty:      'from-pink-400 to-rose-400',
  home:        'from-emerald-400 to-teal-400',
  sports:      'from-green-400 to-emerald-400',
  books:       'from-yellow-400 to-amber-400',
  toys:        'from-red-400 to-pink-400',
  services:    'from-indigo-400 to-violet-400',
  general:     'from-gray-300 to-gray-400',
};

const STOCK_FILTERS = [
  { value: 'all',      label: 'All Items' },
  { value: 'instock',  label: 'In Stock' },
  { value: 'low',      label: 'Low Stock (≤5)' },
  { value: 'outstock', label: 'Out of Stock' },
  { value: 'active',   label: 'Active Only' },
  { value: 'inactive', label: 'Inactive Only' },
];

const EMPTY_FORM = {
  name: '', description: '', price: '', salePrice: '',
  category: 'general', imageUrl: '', stockQuantity: '',
  sku: '', whatsappCatalogId: '', isActive: true,
};

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1], delay },
});

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const fmt = (p) => {
  if (p === null || p === undefined || p === '') return null;
  return `₹${Number(p).toLocaleString('en-IN')}`;
};

const getStockBadge = (qty) => {
  if (qty === null || qty === undefined || qty === '')
    return { label: 'Not tracked', color: 'bg-gray-100 text-gray-500 border-gray-200', dot: 'bg-gray-400' };
  const n = Number(qty);
  if (n <= 0) return { label: 'Out of Stock', color: 'bg-red-50 text-red-600 border-red-200',     dot: 'bg-red-500' };
  if (n <= 5) return { label: `Low (${n})`,   color: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' };
  return        { label: `${n} in stock`,     color: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' };
};

const calcDiscount = (price, sale) => {
  if (!price || !sale || Number(sale) >= Number(price)) return null;
  return Math.round(((Number(price) - Number(sale)) / Number(price)) * 100);
};

// ─────────────────────────────────────────────────────────────────────────────
// IMAGE PREVIEW
// ─────────────────────────────────────────────────────────────────────────────

function ImagePreviewBox({ url }) {
  const [status, setStatus] = useState('idle');
  useEffect(() => {
    if (!url) { setStatus('idle'); return; }
    setStatus('loading');
    const img = new window.Image();
    img.onload  = () => setStatus('ok');
    img.onerror = () => setStatus('error');
    img.src = url;
  }, [url]);

  if (!url || status === 'idle') return null;
  if (status === 'loading') return (
    <div className="w-full h-32 rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-center">
      <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
    </div>
  );
  if (status === 'error') return (
    <div className="w-full h-32 rounded-xl border border-red-200 bg-red-50 flex items-center justify-center gap-2">
      <XCircle className="w-4 h-4 text-red-400" />
      <span className="text-xs text-red-500">Could not load image</span>
    </div>
  );
  return (
    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="overflow-hidden rounded-xl border border-gray-200">
      <img src={url} alt="Preview" className="w-full h-32 object-cover" />
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CATALOG CARD (Grid view)
// ─────────────────────────────────────────────────────────────────────────────

function CatalogCard({ item, onEdit, onDelete, onDuplicate, onToggle, isDeleting }) {
  const [imgErr,   setImgErr]   = useState(false);
  const [expanded, setExpanded] = useState(false);

  const cat      = CATALOG_CATEGORIES.find(c => c.value === item.category) || CATALOG_CATEGORIES[0];
  const stock    = getStockBadge(item.stock_quantity);
  const discount = calcDiscount(item.price, item.sale_price);
  const inactive = item.is_active === false;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className={`group bg-white rounded-2xl border shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden ${
        inactive ? 'border-gray-100 opacity-60' : 'border-gray-100 hover:border-emerald-200'
      }`}
    >
      {/* Top color strip */}
      <div className={`h-1 w-full bg-gradient-to-r ${CAT_GRADIENTS[item.category] || CAT_GRADIENTS.general}`} />

      {/* Image */}
      <div className="relative overflow-hidden">
        {item.image_url && !imgErr ? (
          <img
            src={item.image_url}
            alt={item.name}
            onError={() => setImgErr(true)}
            className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-44 bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col items-center justify-center gap-2">
            <div className="w-14 h-14 rounded-2xl bg-white shadow-sm flex items-center justify-center text-2xl">{cat.emoji}</div>
            <span className="text-xs text-gray-400">No image</span>
          </div>
        )}

        {discount && (
          <div className="absolute top-2.5 left-2.5 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-0.5 shadow">
            <Percent className="w-2.5 h-2.5" /> {discount}% OFF
          </div>
        )}
        {item.whatsapp_catalog_id && (
          <div className="absolute top-2.5 right-2.5 bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow">
            <ShoppingCart className="w-2.5 h-2.5" /> WA
          </div>
        )}
        {inactive && (
          <div className="absolute inset-0 bg-white/40 flex items-center justify-center">
            <span className="bg-black/60 text-white text-[10px] font-semibold px-2.5 py-1 rounded-full">INACTIVE</span>
          </div>
        )}

        {/* Hover actions */}
        <div className="absolute bottom-0 left-0 right-0 p-2 flex justify-end gap-1.5 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-200">
          <button onClick={() => onDuplicate(item)} className="p-2 rounded-xl bg-white/95 shadow text-gray-600 hover:text-blue-600 transition-colors"><Copy className="w-3.5 h-3.5" /></button>
          <button onClick={() => onEdit(item)}      className="p-2 rounded-xl bg-white/95 shadow text-gray-600 hover:text-emerald-600 transition-colors"><Edit2 className="w-3.5 h-3.5" /></button>
          <button onClick={() => onDelete(item.id)} disabled={isDeleting} className="p-2 rounded-xl bg-white/95 shadow text-gray-600 hover:text-red-500 transition-colors disabled:opacity-40">
            {isDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-center gap-1.5 flex-wrap mb-2.5">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${cat.color}`}>{cat.emoji} {cat.label}</span>
          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-medium ${stock.color}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${stock.dot}`} />
            {stock.label}
          </span>
        </div>

        <h3 className="font-bold text-gray-900 text-sm mb-1 line-clamp-1">{item.name}</h3>
        {item.description && <p className="text-xs text-gray-400 line-clamp-2 mb-3 leading-relaxed">{item.description}</p>}

        {/* Price */}
        <div className="flex items-end gap-2 mb-3">
          {item.sale_price && Number(item.sale_price) > 0 ? (
            <><span className="font-bold text-emerald-600 text-lg leading-none">{fmt(item.sale_price)}</span>
              <span className="text-xs text-gray-400 line-through mb-0.5">{fmt(item.price)}</span></>
          ) : item.price && Number(item.price) > 0 ? (
            <span className="font-bold text-gray-900 text-lg leading-none">{fmt(item.price)}</span>
          ) : (
            <span className="text-xs text-gray-400 italic">Price not set</span>
          )}
        </div>

        {item.sku && (
          <div className="mb-3">
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-mono">
              <Hash className="w-2.5 h-2.5" /> {item.sku}
            </span>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-50">
          <button
            onClick={() => onToggle(item)}
            className={`flex items-center gap-1.5 text-xs font-medium transition-colors ${
              inactive ? 'text-gray-400 hover:text-emerald-600' : 'text-emerald-600 hover:text-gray-400'
            }`}
          >
            {inactive ? <><ToggleLeft className="w-4 h-4" /> Inactive</> : <><ToggleRight className="w-4 h-4" /> Active</>}
          </button>
          <button onClick={() => setExpanded(e => !e)} className="flex items-center gap-1 text-xs text-gray-400 hover:text-emerald-600 transition-colors">
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {expanded ? 'Less' : 'Details'}
          </button>
        </div>

        {/* Expanded */}
        <AnimatePresence>
          {expanded && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              <div className="pt-3 mt-1 border-t border-gray-50 space-y-1.5">
                {item.sku && <div className="flex justify-between text-xs"><span className="text-gray-400">SKU</span><span className="font-mono font-medium text-gray-700">{item.sku}</span></div>}
                {item.whatsapp_catalog_id && (
                  <div className="flex justify-between text-xs gap-2">
                    <span className="text-gray-400 flex-shrink-0">WA Catalog ID</span>
                    <span className="font-mono font-medium text-gray-700 text-right truncate max-w-[140px]">{item.whatsapp_catalog_id}</span>
                  </div>
                )}
                {discount && (
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-400">Customer Saves</span>
                    <span className="font-medium text-red-500">{fmt(Number(item.price) - Number(item.sale_price))} ({discount}%)</span>
                  </div>
                )}
                <div className="flex justify-between text-xs">
                  <span className="text-gray-400">Added</span>
                  <span className="text-gray-600">{new Date(item.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LIST ROW
// ─────────────────────────────────────────────────────────────────────────────

function ListRow({ item, onEdit, onDelete, onDuplicate, onToggle, isDeleting }) {
  const cat      = CATALOG_CATEGORIES.find(c => c.value === item.category) || CATALOG_CATEGORIES[0];
  const stock    = getStockBadge(item.stock_quantity);
  const discount = calcDiscount(item.price, item.sale_price);
  const inactive = item.is_active === false;

  return (
    <motion.div layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className={`grid grid-cols-12 gap-3 px-5 py-3.5 items-center hover:bg-gray-50 transition-colors group border-b border-gray-50 last:border-0 ${inactive ? 'opacity-60' : ''}`}
    >
      <div className="col-span-4 flex items-center gap-3 min-w-0">
        <div className="w-11 h-11 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0 border border-gray-100">
          {item.image_url
            ? <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" onError={e => { e.target.style.display='none'; }} />
            : <div className="w-full h-full flex items-center justify-center text-lg">{cat.emoji}</div>
          }
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">{item.name}</p>
          {item.sku && <p className="text-[11px] text-gray-400 font-mono">#{item.sku}</p>}
        </div>
      </div>
      <div className="col-span-2">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${cat.color}`}>{cat.emoji} {cat.label}</span>
      </div>
      <div className="col-span-2">
        {item.sale_price && Number(item.sale_price) > 0 ? (
          <div>
            <p className="text-sm font-bold text-emerald-600">{fmt(item.sale_price)}</p>
            <p className="text-[11px] text-gray-400 line-through">{fmt(item.price)}</p>
            {discount && <span className="text-[10px] bg-red-50 text-red-500 px-1.5 py-0.5 rounded-full">{discount}% off</span>}
          </div>
        ) : (
          <p className="text-sm font-bold text-gray-900">{fmt(item.price) || '—'}</p>
        )}
      </div>
      <div className="col-span-2">
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-medium ${stock.color}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${stock.dot}`} />{stock.label}
        </span>
      </div>
      <div className="col-span-2 flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={() => onToggle(item)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 transition-colors">
          {inactive ? <ToggleLeft className="w-3.5 h-3.5" /> : <ToggleRight className="w-3.5 h-3.5 text-emerald-500" />}
        </button>
        <button onClick={() => onDuplicate(item)} className="p-1.5 rounded-lg hover:bg-blue-50 hover:text-blue-600 text-gray-400 transition-colors"><Copy className="w-3.5 h-3.5" /></button>
        <button onClick={() => onEdit(item)}      className="p-1.5 rounded-lg hover:bg-emerald-50 hover:text-emerald-600 text-gray-400 transition-colors"><Edit2 className="w-3.5 h-3.5" /></button>
        <button onClick={() => onDelete(item.id)} disabled={isDeleting} className="p-1.5 rounded-lg hover:bg-red-50 hover:text-red-500 text-gray-400 transition-colors disabled:opacity-40">
          {isDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
        </button>
      </div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EMPTY STATE
// ─────────────────────────────────────────────────────────────────────────────

function EmptyState({ hasFilter, search, onAdd }) {
  return (
    <motion.div {...fadeUp(0.1)} className="col-span-full py-20 text-center bg-white rounded-2xl border border-gray-100 shadow-sm">
      <div className="w-20 h-20 mx-auto rounded-2xl bg-emerald-50 flex items-center justify-center mb-5">
        <ShoppingCart className="w-10 h-10 text-emerald-300" />
      </div>
      <p className="text-gray-900 font-bold text-lg mb-2">{hasFilter ? 'No products found' : 'Your catalog is empty'}</p>
      <p className="text-sm text-gray-400 mb-6 max-w-xs mx-auto">
        {search ? `No results for "${search}"` : hasFilter ? 'Try adjusting your filters.' : 'Add your first product and start sharing it with customers on WhatsApp.'}
      </p>
      {!hasFilter && (
        <button onClick={onAdd} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors shadow-lg shadow-emerald-200/50">
          <Plus className="w-4 h-4" /> Add First Product
        </button>
      )}
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export default function Catalog() {
  const [items,       setItems]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [dialogOpen,  setDialogOpen]  = useState(false);
  const [editing,     setEditing]     = useState(null);
  const [saving,      setSaving]      = useState(false);
  const [deletingId,  setDeletingId]  = useState(null);
  const [search,      setSearch]      = useState('');
  const [catFilter,   setCatFilter]   = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  const [viewMode,    setViewMode]    = useState('grid');
  const [form,        setForm]        = useState(EMPTY_FORM);
  const [showImgPrev, setShowImgPrev] = useState(false);

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/catalog');        // ✅ /api/catalog — alag endpoint
      setItems(data.items || []);
    } catch {
      setItems([]);
      toast.error('Failed to load catalog');
    } finally {
      setLoading(false);
    }
  };

  // Stats
  const stats = {
    total:      items.length,
    active:     items.filter(i => i.is_active !== false).length,
    outOfStock: items.filter(i => i.stock_quantity !== null && i.stock_quantity !== undefined && Number(i.stock_quantity) <= 0).length,
    value:      items.reduce((s, i) => s + (Number(i.stock_quantity || 0) * Number(i.sale_price || i.price || 0)), 0),
  };

  // Filter
  const filtered = items.filter(item => {
    const q        = item.stock_quantity;
    const inactive = item.is_active === false;
    const mSearch  = !search || [item.name, item.description, item.sku].some(f => f?.toLowerCase().includes(search.toLowerCase()));
    const mCat     = catFilter === 'all' || item.category === catFilter;
    const mStock   =
      stockFilter === 'all'      ? true :
      stockFilter === 'instock'  ? (q !== null && q !== undefined && Number(q) > 5) :
      stockFilter === 'low'      ? (q !== null && q !== undefined && Number(q) > 0 && Number(q) <= 5) :
      stockFilter === 'outstock' ? (q !== null && q !== undefined && Number(q) <= 0) :
      stockFilter === 'active'   ? !inactive :
      stockFilter === 'inactive' ? inactive : true;
    return mSearch && mCat && mStock;
  });

  const hasFilter      = !!(search || catFilter !== 'all' || stockFilter !== 'all');
  const discountPreview = calcDiscount(form.price, form.salePrice);

  // Save
  const handleSave = async () => {
    if (!form.name?.trim() || form.name.trim().length < 2) { toast.error('Product name required (min 2 characters)'); return; }
    if (form.price !== '' && (isNaN(Number(form.price)) || Number(form.price) < 0)) { toast.error('Price must be a valid positive number'); return; }
    if (form.salePrice !== '' && form.salePrice !== null) {
      if (isNaN(Number(form.salePrice)) || Number(form.salePrice) < 0) { toast.error('Sale price must be a valid positive number'); return; }
      if (form.price !== '' && Number(form.salePrice) >= Number(form.price)) { toast.error('Sale price must be less than original price'); return; }
    }

    setSaving(true);
    try {
      const payload = {
        name:              form.name.trim(),
        description:       form.description?.trim() || '',
        price:             form.price !== '' ? Number(form.price) : 0,
        salePrice:         (form.salePrice !== '' && form.salePrice !== null) ? Number(form.salePrice) : null,
        category:          form.category,
        imageUrl:          form.imageUrl?.trim() || '',
        stockQuantity:     form.stockQuantity !== '' ? Number(form.stockQuantity) : null,
        sku:               form.sku?.trim() || '',
        whatsappCatalogId: form.whatsappCatalogId?.trim() || '',
        isActive:          form.isActive,
      };

      if (editing) {
        await api.put(`/catalog/${editing.id}`, payload);     // ✅ PUT /api/catalog/:id
        toast.success('Product updated!');
      } else {
        await api.post('/catalog', payload);                   // ✅ POST /api/catalog
        toast.success('Product added to catalog! 🎉');
      }
      closeDialog();
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  // Delete
  const handleDelete = async (id) => {
    if (!window.confirm('Remove this product from catalog?')) return;
    setDeletingId(id);
    try {
      await api.delete(`/catalog/${id}`);                     // ✅ DELETE /api/catalog/:id
      toast.success('Product removed');
      fetchItems();
    } catch {
      toast.error('Failed to delete product');
    } finally {
      setDeletingId(null);
    }
  };

  // Toggle (dedicated PATCH endpoint)
  const handleToggle = async (item) => {
    const newState = item.is_active === false ? true : false;
    try {
      await api.patch(`/catalog/${item.id}/toggle`, { isActive: newState }); // ✅ PATCH /api/catalog/:id/toggle
      toast.success(newState ? 'Product activated' : 'Product deactivated');
      fetchItems();
    } catch {
      toast.error('Failed to update status');
    }
  };

  // Duplicate
  const handleDuplicate = (item) => {
    setEditing(null);
    setForm({
      name:              `${item.name} (Copy)`,
      description:       item.description || '',
      price:             item.price ?? '',
      salePrice:         item.sale_price ?? '',
      category:          item.category || 'general',
      imageUrl:          item.image_url ?? '',
      stockQuantity:     item.stock_quantity ?? '',
      sku:               '',
      whatsappCatalogId: '',
      isActive:          true,
    });
    setShowImgPrev(false);
    setDialogOpen(true);
  };

  const openAdd = () => { setEditing(null); setForm(EMPTY_FORM); setShowImgPrev(false); setDialogOpen(true); };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name:              item.name || '',
      description:       item.description || '',
      price:             item.price ?? '',
      salePrice:         item.sale_price ?? '',
      category:          item.category || 'general',
      imageUrl:          item.image_url || '',
      stockQuantity:     item.stock_quantity ?? '',
      sku:               item.sku || '',
      whatsappCatalogId: item.whatsapp_catalog_id || '',
      isActive:          item.is_active !== undefined ? item.is_active : true,
    });
    setShowImgPrev(false);
    setDialogOpen(true);
  };

  const closeDialog = () => { setDialogOpen(false); setEditing(null); setForm(EMPTY_FORM); setShowImgPrev(false); };

  // Loading
  if (loading && !items.length) {
    return (
      <div className="h-[calc(100vh-140px)] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="relative w-14 h-14">
            <div className="absolute inset-0 rounded-full border-4 border-emerald-100" />
            <div className="absolute inset-0 rounded-full border-4 border-emerald-500 border-t-transparent animate-spin" />
          </div>
          <p className="text-sm text-gray-400 font-medium">Loading catalog…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">

      {/* Header */}
      <motion.div {...fadeUp(0)} className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight flex items-center gap-2">
            <ShoppingCart className="w-7 h-7 text-emerald-600" />
            Product Catalog
          </h1>
          <p className="mt-1 text-sm text-gray-400">Manage your products & send them to customers via WhatsApp</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={fetchItems} disabled={loading} className="p-2 rounded-xl border border-gray-200 bg-white text-gray-500 hover:border-emerald-300 hover:text-emerald-600 hover:bg-emerald-50 transition-all disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <div className="flex border border-gray-200 rounded-xl overflow-hidden bg-white">
            <button onClick={() => setViewMode('grid')} className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-emerald-50 text-emerald-600' : 'text-gray-400 hover:text-gray-600'}`}><Grid className="w-4 h-4" /></button>
            <button onClick={() => setViewMode('list')} className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-emerald-50 text-emerald-600' : 'text-gray-400 hover:text-gray-600'}`}><List className="w-4 h-4" /></button>
          </div>
          <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-sm font-semibold shadow-lg shadow-emerald-200/60 transition-all">
            <Plus className="w-4 h-4" /> Add Product
          </button>
        </div>
      </motion.div>

      {/* Stats */}
      <motion.div {...fadeUp(0.06)} className="flex flex-wrap gap-3">
        {[
          { icon: Package,     label: 'Total Products',  value: stats.total },
          { icon: CheckCircle, label: 'Active',          value: stats.active },
          { icon: XCircle,     label: 'Out of Stock',    value: stats.outOfStock },
          { icon: TrendingUp,  label: 'Inventory Value', value: `₹${stats.value.toLocaleString('en-IN')}` },
        ].map(({ icon: Icon, label, value }) => (
          <div key={label} className="flex items-center gap-2.5 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl shadow-sm">
            <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center">
              <Icon className="w-3.5 h-3.5 text-emerald-600" />
            </div>
            <div>
              <span className="text-xs text-gray-400 block leading-none mb-0.5">{label}</span>
              <span className="text-sm font-bold text-gray-900 dark:text-white">{value}</span>
            </div>
          </div>
        ))}
      </motion.div>

      {/* Search + Filters */}
      <motion.div {...fadeUp(0.1)} className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <Input placeholder="Search name, description, SKU…" value={search} onChange={e => setSearch(e.target.value)}
            className="pl-10 h-10 rounded-xl border-gray-200 focus:border-emerald-400 text-sm" />
          {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>}
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-44 h-10 rounded-xl border-gray-200 text-sm"><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATALOG_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.emoji} {c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={stockFilter} onValueChange={setStockFilter}>
          <SelectTrigger className="w-40 h-10 rounded-xl border-gray-200 text-sm"><SelectValue placeholder="Stock" /></SelectTrigger>
          <SelectContent>{STOCK_FILTERS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
        {hasFilter && (
          <div className="flex items-center gap-2 self-center">
            <span className="text-xs text-gray-400">{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
            <button onClick={() => { setSearch(''); setCatFilter('all'); setStockFilter('all'); }} className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1">
              <X className="w-3 h-3" /> Clear
            </button>
          </div>
        )}
      </motion.div>

      {/* Info Banner */}
      <motion.div {...fadeUp(0.12)} className="flex items-start gap-3 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl">
        <ShoppingCart className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">WhatsApp Product Catalog</p>
          <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">
            Add products with images and prices. Link your <strong>WhatsApp Catalog ID</strong> from Meta Commerce Manager for native product cards in customer chats.
          </p>
        </div>
      </motion.div>

      {/* Grid */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          <AnimatePresence>
            {filtered.length > 0
              ? filtered.map(item => (
                  <CatalogCard key={item.id} item={item} onEdit={openEdit} onDelete={handleDelete}
                    onDuplicate={handleDuplicate} onToggle={handleToggle} isDeleting={deletingId === item.id} />
                ))
              : <EmptyState hasFilter={hasFilter} search={search} onAdd={openAdd} />
            }
          </AnimatePresence>
        </div>
      )}

      {/* List */}
      {viewMode === 'list' && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden shadow-sm">
          {filtered.length > 0 ? (
            <>
              <div className="grid grid-cols-12 gap-3 px-5 py-3 bg-gray-50 dark:bg-gray-700/50 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100">
                <div className="col-span-4">Product</div>
                <div className="col-span-2">Category</div>
                <div className="col-span-2">Price</div>
                <div className="col-span-2">Stock</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>
              <AnimatePresence>
                {filtered.map(item => (
                  <ListRow key={item.id} item={item} onEdit={openEdit} onDelete={handleDelete}
                    onDuplicate={handleDuplicate} onToggle={handleToggle} isDeleting={deletingId === item.id} />
                ))}
              </AnimatePresence>
            </>
          ) : (
            <EmptyState hasFilter={hasFilter} search={search} onAdd={openAdd} />
          )}
        </div>
      )}

      {/* ═══════════════════════ ADD / EDIT DIALOG ═══════════════════════ */}
      <Dialog open={dialogOpen} onOpenChange={open => !open && closeDialog()}>
        <DialogContent className="sm:max-w-2xl max-h-[95vh] overflow-hidden p-0 rounded-2xl border-gray-100 shadow-2xl">
          <div className="flex flex-col h-full max-h-[95vh]">
            <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-100 flex-shrink-0">
              <DialogTitle className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Package className="w-5 h-5 text-emerald-600" />
                {editing ? 'Edit Product' : 'Add Product to Catalog'}
              </DialogTitle>
            </DialogHeader>

            <div className="overflow-y-auto p-6 space-y-5">

              {/* Name + Category */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Product Name <span className="text-rose-400">*</span></Label>
                  <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Cotton Kurta Set" className="h-10 rounded-xl text-sm" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Category</Label>
                  <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                    <SelectTrigger className="h-10 rounded-xl text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATALOG_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.emoji} {c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Description <span className="text-xs font-normal text-gray-400">(optional)</span></Label>
                <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Short product description customers will see…" rows={3} maxLength={500}
                  className="rounded-xl border-gray-200 focus:border-emerald-400 text-sm resize-none" />
                <p className="text-[11px] text-gray-400 text-right">{form.description?.length || 0}/500</p>
              </div>

              {/* Pricing */}
              <div className="space-y-3">
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Pricing</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-gray-500">Original Price (₹)</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">₹</span>
                      <Input type="number" min="0" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} placeholder="999" className="h-10 rounded-xl text-sm pl-7" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-gray-500">Sale Price (₹) <span className="text-gray-400 font-normal">(optional)</span></Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">₹</span>
                      <Input type="number" min="0" value={form.salePrice} onChange={e => setForm(f => ({ ...f, salePrice: e.target.value }))} placeholder="799" className="h-10 rounded-xl text-sm pl-7" />
                    </div>
                  </div>
                </div>
                <AnimatePresence>
                  {discountPreview && (
                    <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                      className="flex items-center gap-2 p-2.5 bg-green-50 border border-green-200 rounded-xl">
                      <Percent className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <p className="text-xs text-green-700">
                        Customer saves <strong>{fmt(Number(form.price) - Number(form.salePrice))}</strong> — that's <strong>{discountPreview}% off!</strong>
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Stock + SKU */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Stock Qty <span className="text-xs font-normal text-gray-400">(optional)</span></Label>
                  <Input type="number" min="0" value={form.stockQuantity} onChange={e => setForm(f => ({ ...f, stockQuantity: e.target.value }))} placeholder="50" className="h-10 rounded-xl text-sm" />
                  <p className="text-[11px] text-gray-400">Leave blank = not tracked</p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">SKU / Item Code <span className="text-xs font-normal text-gray-400">(optional)</span></Label>
                  <Input value={form.sku} onChange={e => setForm(f => ({ ...f, sku: e.target.value }))} placeholder="e.g. KUR-BLU-M" className="h-10 rounded-xl text-sm font-mono" />
                </div>
              </div>

              {/* Image URL */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Product Image URL <span className="text-xs font-normal text-gray-400">(optional)</span></Label>
                <div className="flex gap-2">
                  <Input value={form.imageUrl} onChange={e => { setForm(f => ({ ...f, imageUrl: e.target.value })); setShowImgPrev(false); }}
                    placeholder="https://cdn.yoursite.com/product.jpg" className="flex-1 h-10 rounded-xl text-sm" />
                  {form.imageUrl && (
                    <button type="button" onClick={() => setShowImgPrev(v => !v)} className="px-3 py-2 rounded-xl border border-gray-200 text-xs text-gray-600 hover:border-emerald-300 hover:text-emerald-600 transition-colors">
                      <Eye className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {showImgPrev && <ImagePreviewBox url={form.imageUrl} />}
                <p className="text-[11px] text-gray-400">JPG/PNG recommended. Square (1:1) images look best on WhatsApp.</p>
              </div>

              {/* WA Catalog ID */}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">WhatsApp Catalog ID <span className="text-xs font-normal text-gray-400">(optional)</span></Label>
                <Input value={form.whatsappCatalogId} onChange={e => setForm(f => ({ ...f, whatsappCatalogId: e.target.value }))}
                  placeholder="From Meta Commerce Manager" className="h-10 rounded-xl text-sm font-mono" />
                <p className="text-[11px] text-gray-400">Links this product to your Meta WhatsApp Catalog for native product cards in customer chats.</p>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center justify-between p-3.5 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700">
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Show in Catalog</p>
                  <p className="text-xs text-gray-400">Inactive products are hidden from customer-facing catalog views</p>
                </div>
                <Switch checked={form.isActive} onCheckedChange={v => setForm(f => ({ ...f, isActive: v }))} className="data-[state=checked]:bg-emerald-500" />
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-1">
                <button onClick={closeDialog} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all">Cancel</button>
                <button onClick={handleSave} disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-sm font-semibold shadow-lg shadow-emerald-200/60 disabled:opacity-60 transition-all">
                  {saving
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
                    : editing
                      ? <><Check className="w-4 h-4" /> Update Product</>
                      : <><Plus className="w-4 h-4" /> Add to Catalog</>
                  }
                </button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}